import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthHeading, BrandButton } from "../auth/components/BrandControls.tsx";
import { AUTH_ROUTES } from "../auth/authHelpers.ts";
import SignUpScreen from "../auth/screens/SignUpScreen.tsx";
import { optOutOfAuditEmails } from "./auditProspect.ts";
import { AuditProfileCard } from "./AuditProfilePanel.tsx";
import { useAuditProspect } from "./AuditProspectContext.tsx";
import { setCookie } from "@/util/cookie.ts";

/** "Made for acme.com · Stop emails about this", under the form. */
function MadeFor({ token, domain }: { token: string; domain: string }) {
    const { state, update } = useAuditProspect();
    const [failed, setFailed] = useState(false);
    const optedOut = state.status === "ready" && state.prospect.optedOut;

    const stop = () => {
        setFailed(false);
        optOutOfAuditEmails(token).then(update, () => setFailed(true));
    };

    return (
        <p className="text-xs text-brand-grey-warm">
            Made for {domain} ·{" "}
            {optedOut ? (
                "We will not email you about this again."
            ) : (
                <button type="button" className="underline" onClick={stop}>
                    Stop emails about this
                </button>
            )}
            {failed && " That did not work. Please try again."}
        </p>
    );
}

/**
 * Where an audit link lands (specs/icp-fit/outbound-audit.md §2): the
 * sign-up form, with the profile we drafted from the prospect's website
 * beside it (AuditProfilePanel). A link that no longer opens anything still
 * leads somewhere: the ordinary sign-up.
 */
export default function AuditLandingScreen() {
    const navigate = useNavigate();
    const { token, state } = useAuditProspect();
    const isOpen = state.status === "ready";

    // Whichever way they sign up from here, with a code or with Google, the
    // new account is this prospect's. Sent once, with the first request after
    // the sign-up (baseQuery.ts), and cleared when that succeeds.
    useEffect(() => {
        if (token !== null && isOpen) {
            setCookie("auditProspect", token, 14);
        }
    }, [token, isOpen]);

    if (token === null || state.status === "closed") {
        return (
            <>
                <AuthHeading
                    title="This link has expired"
                    subtitle="The profile behind it is no longer available. You can still create an account and draft one from your website."
                />
                <BrandButton onClick={() => navigate(AUTH_ROUTES.signUp)}>
                    Create an account
                </BrandButton>
            </>
        );
    }
    if (state.status === "failed") {
        return (
            <AuthHeading
                title="We could not open this page"
                subtitle={state.message}
            />
        );
    }
    if (state.status === "loading") {
        return <p className="text-sm text-brand-grey-warm">One moment…</p>;
    }

    return (
        <>
            <SignUpScreen
                title="Is this who you sell to?"
                subtitle={`We read ${state.prospect.domain} and drafted your ideal customer profile. Change anything that is off, then create an account to put it to work.`}
                // As little to fill in as possible: an address is enough.
                askName={false}
                afterHeading={
                    // A phone has no right half: the profile is the offer, so
                    // it goes under the heading there.
                    <div className="lg:hidden">
                        <AuditProfileCard
                            token={token}
                            prospect={state.prospect}
                        />
                    </div>
                }
            />
            <MadeFor token={token} domain={state.prospect.domain} />
        </>
    );
}
